import { createElement, useEffect, useMemo, useState } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Logo } from "./shared";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import {
  LayoutDashboard,
  Building2,
  Users,
  Wrench,
  ClipboardList,
  ScrollText,
  LogOut,
  ChevronDown,
  ChevronRight,
  ShieldCheck,
  BookOpen,
  IndianRupee,
  TrendingUp,
  CreditCard,
  GraduationCap,
  CalendarClock,
  UserCog,
  BriefcaseBusiness,
  KeyRound,
  ContactRound,
  Settings2,
  Landmark,
  ChartNoAxesCombined,
  SlidersHorizontal,
  Briefcase,
  ReceiptText,
  FileText,
  MapPin,
  BookOpenCheck,
  Scale,
  Handshake,
  Boxes,
  FlaskConical,
  Menu,
  X,
} from "lucide-react";

// Each leaf: [path (relative to /admin), Icon, label, rbacModule]
// Each group: { id, label, Icon, items: [leaf | group] }
const NAV_TREE = [
  {
    id: "dashboard",
    label: "Dashboard",
    Icon: LayoutDashboard,
    items: [
      ["", LayoutDashboard, "Management Dashboard", "management_dashboard"],
      ["reports", ChartNoAxesCombined, "Management Reports", "management_reports"],
    ],
  },
  {
    id: "shivkrupa",
    label: "Shivkrupa Enterprises",
    Icon: Briefcase,
    items: [
      {
        id: "finance",
        label: "Finance Management",
        Icon: Landmark,
        items: [
          ["company-finance", Landmark, "Accounting Dashboard", "company_accounting"],
          ["company-finance?tab=sales", FileText, "Sales Invoices", "company_accounting"],
          ["company-finance?tab=receipts", ReceiptText, "Receipts", "company_accounting"],
          ["company-finance?tab=payments", Handshake, "Payments", "company_payments"],
          ["company-finance?tab=journals", BookOpenCheck, "Journals", "company_accounting"],
          ["company-finance?tab=ledger", BookOpen, "Ledger View", "company_accounting"],
          ["company-finance?tab=masters", Boxes, "Masters (Party / Item / Unit)", "company_accounting"],
          ["company-finance?tab=accounts", Landmark, "Chart of Accounts", "company_accounting"],
          ["company-finance?tab=reports", Scale, "Trial Balance, P&L & Balance Sheet", "financial_reports"],
        ],
      },
      {
        id: "staff",
        label: "Staff Management",
        Icon: BriefcaseBusiness,
        items: [
          ["staff", BriefcaseBusiness, "Staff", "staff"],
          ["departments", Building2, "Departments", "departments"],
          ["roles", KeyRound, "Roles & Permissions", "roles"],
        ],
      },
    ],
  },
  {
    id: "crm",
    label: "CRM & Portal Management",
    Icon: ContactRound,
    items: [
      ["crm", ContactRound, "CRM Dashboard", "crm_leads"],
      ["societies", Building2, "Societies", "societies"],
      ["applications", ClipboardList, "Applications", "applications"],
      ["provider-applications", ClipboardList, "Provider Applications", "applications"],
      ["leads", Users, "CRM Leads & Follow-ups", "crm_leads"],
      ["users", UserCog, "Users & Access", "users_access"],
      ["requirements", ClipboardList, "Requirements", "requirements"],
      {
        id: "vendors",
        label: "Vendors",
        Icon: Wrench,
        items: [
          ["vendors", Wrench, "Vendor Management", "vendors"],
          ["vendor-legal", FileText, "Vendor Legal & Schedules", "vendors"],
          ["vendor-pilots", MapPin, "Technician Pilots", "vendors"],
          ["pilot-bookings", BookOpenCheck, "Pilot Bookings", "vendors"],
          ["payment-settings", CreditCard, "Payment Settings", "vendors"],
        ],
      },
      {
        id: "experts",
        label: "Experts",
        Icon: GraduationCap,
        items: [
          ["experts", GraduationCap, "Expert Management", "experts"],
          ["consultation-requests", ClipboardList, "Consultation Requests", "experts"],
          ["appointments", CalendarClock, "Expert Appointments", "experts"],
          ["consultation-sessions", CalendarClock, "Consultation Sessions", "experts"],
        ],
      },
      ["knowledge", BookOpen, "Knowledge & SEO", "knowledge"],
    ],
  },
  {
    id: "revenue",
    label: "Revenue & Commercial",
    Icon: TrendingUp,
    items: [
      ["commercial", Landmark, "Commercial Controls", "commercial_dashboard"],
      ["commercial-quotes", FileText, "Commercial Quotations", "commercial_quotes"],
      ["subscriptions", CreditCard, "Society Subscriptions", "subscriptions"],
      ["subscriptions?tab=vendors", Handshake, "Vendor Subscriptions & Waivers", "subscriptions"],
      ["expert-commercial", GraduationCap, "Expert Commercials", "commercial_dashboard"],
      ["monetization", IndianRupee, "Monetization", "monetization"],
      ["revenue", TrendingUp, "Revenue Reports", "revenue"],
    ],
  },
  {
    id: "platform",
    label: "Platform Configuration",
    Icon: Boxes,
    items: [
      ["services", Settings2, "Service Master", "services"],
      ["resident-services", Wrench, "Resident Doorstep Services", "services"],
      ["features", SlidersHorizontal, "Society Features", "feature_registry"],
      ["features?tab=registry", Boxes, "Feature Registry", "feature_registry"],
      ["features?tab=requests", ClipboardList, "Feature Requests", "feature_registry"],
    ],
  },
  {
    id: "system",
    label: "System & Testing",
    Icon: FlaskConical,
    items: [["audit", ScrollText, "Audit Logs", "audit_logs"]],
  },
];

const STORAGE_KEY = "msm_admin_nav_open";

const splitPath = (p) => {
  const [path, query = ""] = p.split("?");
  return { path, query };
};

const filterTree = (items, canView) =>
  items
    .map((item) => {
      if (Array.isArray(item)) return canView(item[3]) ? item : null;
      const children = filterTree(item.items, canView);
      return children.length ? { ...item, items: children } : null;
    })
    .filter(Boolean);

const isLeafActive = (leaf, location) => {
  const { path, query } = splitPath(leaf[0]);
  const current = location.pathname.replace(/^\/admin\/?/, "").split("/")[0];
  if (current !== path) return false;
  const currentTab = new URLSearchParams(location.search).get("tab");
  const leafTab = new URLSearchParams(query).get("tab");
  return (leafTab || null) === (currentTab || null);
};

const groupHasActive = (group, location) =>
  group.items.some((item) =>
    Array.isArray(item) ? isLeafActive(item, location) : groupHasActive(item, location),
  );

const groupIds = (items, acc = []) => {
  items.forEach((item) => {
    if (!Array.isArray(item)) {
      acc.push(item.id);
      groupIds(item.items, acc);
    }
  });
  return acc;
};

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const canView = (module) =>
    user?.role === "super_admin" || user?.permissions?.[module]?.view;
  const tree = useMemo(() => filterTree(NAV_TREE, canView), [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const [open, setOpen] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "null");
      if (saved && typeof saved === "object") return saved;
    } catch {
      // ignore
    }
    return { dashboard: true };
  });

  useEffect(() => {
    const expand = {};
    const walk = (items) =>
      items.forEach((item) => {
        if (!Array.isArray(item) && groupHasActive(item, location)) {
          expand[item.id] = true;
          walk(item.items);
        }
      });
    walk(tree);
    if (Object.keys(expand).length) setOpen((prev) => ({ ...prev, ...expand }));
    setMobileOpen(false);
  }, [location.pathname, location.search, tree]);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(open));
    } catch {
      // ignore
    }
  }, [open]);

  const toggle = (id) => setOpen((prev) => ({ ...prev, [id]: !prev[id] }));
  const hasAny = tree.length > 0;

  const navContent = (
    <nav className="flex flex-col gap-1 p-3" data-testid="admin-sidebar-nav">
      {tree.map((group) => (
        <NavGroup
          depth={0}
          group={group}
          key={group.id}
          location={location}
          open={open}
          toggle={toggle}
        />
      ))}
      {!hasAny && (
        <div
          className="px-3 py-4 text-xs leading-relaxed text-white/60"
          data-testid="admin-no-permissions"
        >
          No admin modules are assigned to your staff role.
        </div>
      )}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background flex">
      <aside className="hidden lg:flex flex-col w-[292px] shrink-0 border-r bg-primary text-white h-screen sticky top-0">
        <div className="h-16 flex items-center px-4 border-b border-white/10 shrink-0">
          <Logo light />
        </div>
        <div className="px-4 py-3 text-xs text-white/50 flex items-center gap-2 shrink-0">
          <ShieldCheck className="w-4 h-4" /> Super Admin Console
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain [scrollbar-width:thin]">
          {navContent}
        </div>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 flex lg:hidden" data-testid="admin-mobile-sidebar">
          <div className="w-[280px] bg-primary text-white flex flex-col h-full shadow-2xl">
            <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
              <Logo light />
              <button
                aria-label="Close menu"
                className="p-2 rounded-lg hover:bg-white/10"
                data-testid="admin-mobile-close"
                onClick={() => setMobileOpen(false)}
                type="button"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">{navContent}</div>
          </div>
          <button
            aria-label="Close menu overlay"
            className="flex-1 bg-black/40"
            onClick={() => setMobileOpen(false)}
            type="button"
          />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-40 glass border-b h-16 flex items-center justify-between px-5">
          <div className="flex items-center gap-2 lg:hidden">
            <button
              aria-label="Open menu"
              className="p-2 rounded-lg hover:bg-muted"
              data-testid="admin-mobile-menu-toggle"
              onClick={() => setMobileOpen(true)}
              type="button"
            >
              <Menu className="w-5 h-5" />
            </button>
            <Logo />
          </div>
          <div className="font-display font-bold hidden lg:block">
            MySocietyMitra Admin
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-muted"
                data-testid="admin-user-menu"
              >
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback>{user?.name?.[0]}</AvatarFallback>
                </Avatar>
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-3 py-2">
                <div className="font-medium text-sm">{user?.name}</div>
                <div className="text-xs text-muted-foreground">
                  {user?.email}
                </div>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={logout}
                className="text-destructive"
                data-testid="admin-logout"
              >
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className="p-5 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function NavGroup({ group, depth, open, toggle, location }) {
  const expanded = !!open[group.id];
  const active = groupHasActive(group, location);
  const Icon = group.Icon;
  return (
    <div className={depth ? "mt-0.5" : "mt-1"} data-testid={`admin-nav-group-${group.id}`}>
      <button
        aria-expanded={expanded}
        className={`w-full flex items-center gap-3 rounded-lg text-sm font-semibold transition-colors ${
          depth ? "px-3 py-2 text-[13px]" : "px-3 py-2.5"
        } ${active && !expanded ? "bg-white/15 text-white" : "text-white/85 hover:bg-white/10"}`}
        data-testid={`admin-nav-group-toggle-${group.id}`}
        onClick={() => toggle(group.id)}
        type="button"
      >
        <Icon className={depth ? "w-4 h-4" : "w-[18px] h-[18px]"} />
        <span className="flex-1 text-left truncate">{group.label}</span>
        <ChevronRight
          className={`w-4 h-4 text-white/50 transition-transform duration-200 ${expanded ? "rotate-90" : ""}`}
        />
      </button>
      {expanded && (
        <div
          className={`mt-0.5 ml-[18px] flex flex-col gap-0.5 border-l border-white/15 pl-2`}
          data-testid={`admin-nav-group-items-${group.id}`}
        >
          {group.items.map((item) =>
            Array.isArray(item) ? (
              <NavLeaf item={item} key={item[0]} location={location} />
            ) : (
              createElement(NavGroup, {
                depth: depth + 1,
                group: item,
                key: item.id,
                location,
                open,
                toggle,
              })
            ),
          )}
        </div>
      )}
    </div>
  );
}

function NavLeaf({ item, location }) {
  const [to, Icon, label] = item;
  const active = isLeafActive(item, location);
  const { path, query } = splitPath(to);
  const testId = `admin-nav-${path || "dashboard"}${query ? `-${new URLSearchParams(query).get("tab")}` : ""}`;
  return (
    <Link
      aria-current={active ? "page" : undefined}
      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors ${
        active ? "bg-white text-primary" : "text-white/70 hover:bg-white/10 hover:text-white"
      }`}
      data-testid={testId}
      to={`/admin/${to}`}
    >
      <Icon className="w-4 h-4 shrink-0" />
      <span className="truncate">{label}</span>
    </Link>
  );
}
